import { useState } from "react";
import type { MediaItem } from "@gooner/shared";
import { track } from "../../analytics";
import { userApi } from "../../lib/api";
import { useFapsStore } from "../../lib/faps";
import { useSession } from "../../lib/session";
import { CrownMark } from "../ui/Crown";
import { cx } from "../ui/primitives";
import { AuthModal } from "./AuthModal";

/** Where a card's crown was tapped — a closed set, so `fap_saved` carries a surface and
 *  never anything about the clip. */
export type FapCrownSurface = "browse" | "collection" | "profile";

/**
 * The thumbnail crown (ClipCard's): spammable and idempotent — a tap faps the clip and
 * never un-faps it, and the reply's balance ticks the Faps pill when the tap paid. It
 * starts muted on every load; it has no fap status of its own to seed from. Signed out,
 * the tap asks for an account instead.
 */
export function FapCrownButton({ clip, surface }: { clip: MediaItem; surface: FapCrownSurface }) {
  const user = useSession((s) => s.user);
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  async function add() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const r = await userApi.fapClick(clip);
      track("fap_saved", { surface: surface });
      setAdded(true);
      // The whole reply, paid or not — the store decides what each answer means.
      useFapsStore.getState().applyClick(r);
    } catch {
      /* idempotent; ignore transient failures */
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label={added ? "Fapped" : "Fap this clip"}
        title={!user ? "Sign in to fap" : added ? "Fapped ✓" : "Fap — save to your library"}
        onClick={(e) => {
          e.stopPropagation();
          void add();
        }}
        className={cx(
          "flex h-6 w-6 items-center justify-center rounded-md bg-black/60 transition-colors hover:bg-[var(--color-accent)]",
          added ? "text-[#f5c518]" : "text-zinc-200",
        )}
      >
        <CrownMark size={14} tone={added ? "gold" : "muted"} />
      </button>
      {authOpen && (
        <AuthModal
          initialMode="register"
          reason="Create a free account to fap this clip."
          onClose={() => setAuthOpen(false)}
        />
      )}
    </>
  );
}
